<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { sanitizeLabel } from 'shared/helpers/sanitizeData';
import Hotkey from 'dashboard/components/base/Hotkey.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LabelDropdownItem from './LabelDropdownItem.vue';

const props = defineProps({
  accountLabels: {
    type: Array,
    default: () => [],
  },
  selectedLabels: {
    type: Array,
    default: () => [],
  },
  allowCreation: {
    type: Boolean,
    default: false,
  },
  creationTeams: {
    type: Array,
    default: () => [],
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add', 'remove', 'create']);
const { t } = useI18n();

const searchbar = ref(null);
const createTitleInput = ref(null);
const search = ref('');
const inlineFormVisible = ref(false);
const createTitle = ref('');
const createColor = ref('');
const creationTeamId = ref('');

const createLabelPlaceholder = computed(() => {
  const label = t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL');
  return search.value ? `${label}:` : label;
});
const filteredActiveLabels = computed(() => {
  if (!search.value) return props.accountLabels;

  return picoSearch(props.accountLabels, search.value, ['title']);
});
const noResult = computed(() => filteredActiveLabels.value.length === 0);
const parsedSearch = computed(() => sanitizeLabel(search.value));
const hasExactMatchInResults = computed(() =>
  filteredActiveLabels.value.some(label => label.title === parsedSearch.value)
);
const shouldShowCreate = computed(
  () => props.allowCreation && filteredActiveLabels.value.length < 3
);
const hasMultipleCreationTeams = computed(() => props.creationTeams.length > 1);
const creationTeamName = computed(() => props.creationTeams[0]?.name || '');

const focusSearch = () => searchbar.value?.focus();
const onAdd = label => emit('add', label);
const onRemove = label => emit('remove', label);
const onAddRemove = label => {
  if (props.selectedLabels.includes(label.title)) {
    onRemove(label.title);
  } else {
    onAdd(label);
  }
};

const showCreate = async () => {
  createTitle.value = parsedSearch.value;
  createColor.value = '';
  creationTeamId.value =
    props.creationTeams.length === 1 ? props.creationTeams[0].id : '';
  inlineFormVisible.value = true;
  await nextTick();
  createTitleInput.value?.focus();
};

const hideInlineForm = async () => {
  inlineFormVisible.value = false;
  await nextTick();
  focusSearch();
};

const submitInlineCreate = () => {
  if (props.isCreating) return;

  const title = sanitizeLabel(createTitle.value);
  if (!title || !creationTeamId.value) return;

  const label = { title, team_id: creationTeamId.value };
  const color = createColor.value.trim();
  if (color) label.color = color;
  emit('create', label);
};

onMounted(focusSearch);
</script>

<template>
  <div class="flex max-h-[12.5rem] w-full flex-col">
    <div class="mb-1 flex items-center justify-center">
      <h4
        class="m-0 flex-grow overflow-hidden text-ellipsis whitespace-nowrap text-sm text-n-slate-12"
      >
        {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.TITLE') }}
      </h4>
      <Hotkey
        custom-class="border border-solid text-n-slate-12 bg-n-slate-2 text-xxs border-n-strong flex-shrink-0"
      >
        {{ 'L' }}
      </Hotkey>
    </div>
    <div class="mb-2 max-h-8 flex-auto flex-shrink-0 flex-grow-0">
      <input
        ref="searchbar"
        v-model="search"
        type="text"
        class="h-8 w-full rounded-lg border border-n-strong bg-n-alpha-2 px-2 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9 focus:border-n-brand"
        autofocus
        :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
      />
    </div>
    <div class="flex flex-auto flex-grow flex-shrink items-start overflow-auto">
      <div class="my-1 w-full">
        <woot-dropdown-menu>
          <LabelDropdownItem
            v-for="label in filteredActiveLabels"
            :key="label.title"
            :title="label.title"
            :color="label.color"
            :selected="selectedLabels.includes(label.title)"
            @select-label="onAddRemove(label)"
          />
        </woot-dropdown-menu>
        <div
          v-if="noResult"
          class="flex justify-center px-2.5 py-4 text-xs font-medium text-n-slate-11"
        >
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') }}
        </div>

        <form
          v-if="inlineFormVisible"
          data-testid="inline-label-form"
          class="flex flex-col gap-2 border-t border-n-weak pt-2"
          @submit.prevent="submitInlineCreate"
        >
          <p
            v-if="!hasMultipleCreationTeams"
            class="m-0 text-xs text-n-slate-11"
          >
            {{
              $t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_FOR_TEAM', {
                team: creationTeamName,
              })
            }}
          </p>
          <div v-else class="grid gap-1">
            <label
              for="inline-label-team"
              class="text-xs font-medium text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.TEAM') }}
            </label>
            <select
              id="inline-label-team"
              v-model="creationTeamId"
              data-testid="inline-label-team"
              required
              class="h-8 w-full rounded-lg border border-n-strong bg-n-alpha-2 px-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            >
              <option disabled value="">
                {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.SELECT_TEAM') }}
              </option>
              <option
                v-for="team in creationTeams"
                :key="team.id"
                :value="team.id"
              >
                {{ team.name }}
              </option>
            </select>
          </div>
          <input
            ref="createTitleInput"
            v-model="createTitle"
            data-testid="inline-label-title"
            type="text"
            required
            class="h-8 w-full rounded-lg border border-n-strong bg-n-alpha-2 px-2 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9 focus:border-n-brand"
            :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.NAME')"
          />
          <input
            v-model="createColor"
            data-testid="inline-label-color"
            type="text"
            pattern="#[0-9a-fA-F]{6}"
            class="h-8 w-full rounded-lg border border-n-strong bg-n-alpha-2 px-2 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9 focus:border-n-brand"
            :placeholder="
              $t('CONTACT_PANEL.LABELS.LABEL_SELECT.COLOR_OPTIONAL')
            "
          />
          <div class="flex justify-end gap-1">
            <NextButton
              type="button"
              slate
              sm
              ghost
              :label="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CANCEL')"
              :disabled="isCreating"
              @click="hideInlineForm"
            />
            <NextButton
              type="submit"
              sm
              :label="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_AND_ASSIGN')"
              :is-loading="isCreating"
              :disabled="!sanitizeLabel(createTitle) || !creationTeamId"
            />
          </div>
        </form>

        <div
          v-else-if="allowCreation && shouldShowCreate"
          class="flex border-t border-solid border-n-weak pt-1"
        >
          <NextButton
            icon="i-lucide-plus"
            slate
            sm
            ghost
            :label="`${createLabelPlaceholder} ${parsedSearch}`"
            :disabled="hasExactMatchInResults"
            @click="showCreate"
          />
        </div>
      </div>
    </div>
  </div>
</template>
